import { type ReactNode } from 'react';
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
} from '@azure/msal-react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './useAuth';
import type { UserRole } from '../types/user';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRole?: UserRole;
}

/**
 * Route gate enforcing authentication and (optionally) role membership.
 *
 * - Unauthenticated callers are redirected to `/signin` with `state.from` so
 *   the sign-in flow can return them to the original target.
 * - Authenticated-but-unauthorized callers are redirected to `/access-denied`
 *   with `state.from`, `state.requiredRole`, and `state.reason = 'forbidden'`
 *   so the UI can surface a meaningful message and the role-mismatch is
 *   captured for audit (telemetry forwarding happens in the `AccessDenied` page).
 *
 * Backend enforcement remains the security source of truth — the role check
 * here is UX layering only.
 */
export function ProtectedRoute({
  children,
  requiredRole,
}: ProtectedRouteProps) {
  const location = useLocation();
  const { roles } = useAuth();

  return (
    <>
      <AuthenticatedTemplate>
        {requiredRole && !roles.includes(requiredRole) ? (
          <Navigate
            to="/access-denied"
            state={{
              from: location,
              requiredRole,
              reason: 'forbidden',
            }}
            replace
          />
        ) : (
          children
        )}
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <Navigate to="/signin" state={{ from: location }} replace />
      </UnauthenticatedTemplate>
    </>
  );
}
